import React, { createContext, useContext, useState, useEffect } from 'react';

export enum Colors {
  WHITE = 'WHITE',
  BLACK = 'BLACK',
  RED = 'RED',
  BROWN = 'BROWN',
  BLUE = 'BLUE',
  DARK_BLUE = 'DARK_BLUE',
  ORANGE = 'ORANGE',
  GOLD = 'GOLD',
  GREEN = 'GREEN',
  YELLOW = 'YELLOW',
  PURPLE = 'PURPLE',
}

export interface CustomizationItem {
  name: string;
  max_length: number;
  customText: string;
  color?: Colors;
}
export enum ProductImageType {
  LARGE = 'LARGE',
  SMALL = 'SMALL',
  DETAIL = 'DETAIL',
  ANIMATION = 'ANIMATION',
  LIST = 'LIST',
}

export enum ProductType {
  SNAPCHARM = 'SNAPCHARM',
  SPORTS = 'SPORTS',
  LOCALS = 'LOCALS',
  CUSTOM = 'CUSTOM'
}

export enum ProductCategory {
  NECKLACES = 'NECKLACES',
  MAGNETS = 'MAGNETS',
  BRACELETS = 'BRACELETS',
  KEYCHAIN = 'KEYCHAIN',
  WATCHES = 'WATCHES',
  PENDANTS = 'PENDANTS',
  EARRINGS = 'EARRINGS',
  DISPLAY = 'DISPLAY',
  WALL_ART = 'WALL_ART',
  OTHER = 'OTHER'
}

export enum ProductSubCategory {
  ANIMALS = 'ANIMALS',
  SPACE = 'SPACE',
  SEASONAL = 'SEASONAL',
  SPORTS_JERSEY_NAME = 'SPORTS_JERSEY_NAME',
  SPORTS_JERSEY_NUMBER = 'SPORTS_JERSEY_NUMBER',
  SPORTS_JERSEY_TEAM_NAME = 'SPORTS_JERSEY_TEAM_NAME',
  SPORTS_FOOTBALL = 'SPORTS_FOOTBALL',
  LOCAL_SIGNS_FAYETTEVILLE_ARKANSAS = 'LOCAL_SIGNS_FAYETTEVILLE_ARKANSAS',
  LOCAL_SIGNS_CUSTOMIZABLE = 'LOCAL_SIGNS_CUSTOMIZABLE',
  CUSTOM = 'CUSTOM'
}

export interface ProductImage<T extends ProductImageType> {
  image_path: string;
  image_type: ProductImageType;
}

export interface Product {
  id: string;
  name: string;
  height_inches: number;
  width_inches: number;
  depth_inches: number;
  price: number;
  wholesale_price: number;
  images: ProductImage<ProductImageType>[];
  weight_grams: number;
  product_type: ProductType;
  category?: string;
  sub_category?: string;
  description?: string;
  is_customizeable?: boolean;
  customization_items?: CustomizationItem[];
  text_options?: string[];
  enabled?: boolean;
}

export interface CartItem {
  id: string;
  name: string;
  height_inches: number;
  width_inches: number;
  depth_inches: number;
  price: number;
  wholesale_price?: number;
  quantity: number;
  weight_grams: number;
  product_type: ProductType;
  is_customizeable?: boolean;
  customization_items?: CustomizationItem[];
}

export interface CustomerInfo {
  name: string;
  email: string;
  memberId?: string;
}

export interface ShippingAddress {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
}

export interface OrderInfo {
  createdAt: Date;
  checkout_request_id: string;
  isPickup: boolean;
}

interface CartContextType {
  cart: CartItem[];
  customerInfo: CustomerInfo | null;
  shippingAddress: ShippingAddress | null;
  orderInfo: OrderInfo | null;
  is_customizeable?: boolean;
  customization_items?: CustomizationItem[];
  addToCart: (item: CartItem) => void;
  removeFromCart: (id: string) => void;
  clearCart: () => void;
  setCustomerInfo: (info: CustomerInfo) => void;
  setShippingAddress: (address: ShippingAddress) => void;
  setOrderInfo: (info: OrderInfo) => void;
  clearCustomerInfo: () => void;
  clearShippingAddress: () => void;
  clearOrderInfo: () => void;
  clearAll: () => void;
  setIsCustomizable: (is_customizeable: boolean) => void;
  setCustomizationItemsState: (customization_items: CustomizationItem[]) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cart, setCart] = useState<CartItem[]>(() => {
    // Initialize cart from localStorage if available
    const savedCart = localStorage.getItem('cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });

  const [customerInfo, setCustomerInfoState] = useState<CustomerInfo | null>(() => {
    // Initialize customer info from localStorage if available
    const savedCustomerInfo = localStorage.getItem('customerInfo');
    return savedCustomerInfo ? JSON.parse(savedCustomerInfo) : null;
  });

  const [shippingAddress, setShippingAddressState] = useState<ShippingAddress | null>(() => {
    // Initialize shipping address from localStorage if available
    const savedShippingAddress = localStorage.getItem('shippingAddress');
    return savedShippingAddress ? JSON.parse(savedShippingAddress) : null;
  });

  const [orderInfo, setOrderInfoState] = useState<OrderInfo | null>(() => {
    // Initialize order info from localStorage if available
    const savedOrderInfo = localStorage.getItem('orderInfo');
    if (savedOrderInfo) {
      const parsed = JSON.parse(savedOrderInfo);
      return {
        ...parsed,
        createdAt: new Date(parsed.createdAt)
      };
    }
    return null;
  });

  const [is_customizeable, setIsCustomizableState] = useState<boolean | undefined>(undefined);
  const [customization_items, setCustomizationItemsState] = useState<CustomizationItem[] | undefined>(undefined);

  // Save cart to localStorage whenever it changes (exclude wholesale_price)
  useEffect(() => {
    // Create a sanitized version without wholesale_price for customer-facing storage
    const sanitizedCart = cart.map(({ wholesale_price, ...item }) => item);
    localStorage.setItem('cart', JSON.stringify(sanitizedCart));
  }, [cart]);

  // Save customer info to localStorage whenever it changes
  useEffect(() => {
    if (customerInfo) {
      localStorage.setItem('customerInfo', JSON.stringify(customerInfo));
    } else {
      localStorage.removeItem('customerInfo');
    }
  }, [customerInfo]);

  // Save shipping address to localStorage whenever it changes
  useEffect(() => {
    if (shippingAddress) {
      localStorage.setItem('shippingAddress', JSON.stringify(shippingAddress));
    } else {
      localStorage.removeItem('shippingAddress');
    }
  }, [shippingAddress]);

  // Save order info to localStorage whenever it changes
  useEffect(() => {
    if (orderInfo) {
      localStorage.setItem('orderInfo', JSON.stringify(orderInfo));
    } else {
      localStorage.removeItem('orderInfo');
    }
  }, [orderInfo]);

  // Helper function to create a unique ID based on product ID and customization values
  const createUniqueCartItemId = (productId: string, customizationItems?: CustomizationItem[]): string => {
    if (!customizationItems || customizationItems.length === 0) {
      return productId;
    }
    
    // Create a string from all customization values
    const customizationString = customizationItems
      .map(item => `${item.name}:${item.customText}`)
      .sort() // Sort to ensure consistent ordering
      .join('|');
    
    return `${productId}_${customizationString}`;
  };

  const addToCart = (item: CartItem) => {
    setCart(prevCart => {
      // Create a unique ID that includes customization values
      const uniqueId = createUniqueCartItemId(item.id, item.customization_items);
      const itemWithUniqueId = { ...item, id: uniqueId };
      
      const existingItem = prevCart.find(i => i.id === uniqueId);
      if (existingItem) {
        // If item with same ID and customizations exists, update its quantity
        return prevCart.map(i =>
          i.id === uniqueId ? { ...i, quantity: i.quantity + item.quantity } : i
        );
      }
      // If item is new (different customizations), add it and sort the entire cart
      const newCart = [...prevCart, itemWithUniqueId];
      return newCart.sort((a, b) => a.name.localeCompare(b.name));
    });
  };

  const removeFromCart = (id: string) => {
    setCart(prevCart => prevCart.filter(item => item.id !== id));
  };

  const clearCart = () => {
    setCart([]);
  };

  const setCustomerInfo = (info: CustomerInfo) => {
    setCustomerInfoState(info);
  };

  const setShippingAddress = (address: ShippingAddress) => {
    setShippingAddressState(address);
  };

  const setOrderInfo = (info: OrderInfo) => {
    setOrderInfoState(info);
  };

  const clearCustomerInfo = () => {
    setCustomerInfoState(null);
  };

  const clearShippingAddress = () => {
    setShippingAddressState(null);
  };

  const clearOrderInfo = () => {
    setOrderInfoState(null);
  };

  const clearAll = () => {
    setCart([]);
    setCustomerInfoState(null);
    setShippingAddressState(null);
    setOrderInfoState(null);
  };

  const setIsCustomizable = (is_customizeable: boolean) => {
    setIsCustomizableState(is_customizeable);
  };

  return (
    <CartContext.Provider value={{ 
      cart, 
      customerInfo, 
      shippingAddress,
      orderInfo,
      is_customizeable,
      customization_items,
      addToCart, 
      removeFromCart, 
      clearCart,
      setCustomerInfo,
      setShippingAddress,
      setOrderInfo,
      clearCustomerInfo,
      clearShippingAddress,
      clearOrderInfo,
      clearAll,
      setIsCustomizable,
      setCustomizationItemsState
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
